import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Image as ImageIcon, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

interface ProfileImageGalleryProps {
  mainImage: string;
  galleryImages: string[];
  profileName: string;
}

const ProfileImageGallery: React.FC<ProfileImageGalleryProps> = ({
  mainImage,
  galleryImages,
  profileName
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  
  // Combine main image with gallery images and filter out empty values
  const allImages = [mainImage, ...galleryImages].filter(Boolean);
  
  // If no images are available, show placeholder
  if (allImages.length === 0) {
    return (
      <Card>
        <CardContent className="p-6 flex flex-col items-center justify-center aspect-[3/4]">
          <ImageIcon className="w-16 h-16 text-muted-foreground mb-2" />
          <p className="text-muted-foreground">Aucune image disponible</p>
        </CardContent>
      </Card>
    );
  }

  const navigateImage = (direction: 'prev' | 'next') => {
    if (direction === 'prev') {
      setSelectedIndex(prev => prev === 0 ? allImages.length - 1 : prev - 1);
    } else {
      setSelectedIndex(prev => prev === allImages.length - 1 ? 0 : prev + 1);
    }
  };
  
  return (
    <div className="space-y-4">
      {/* Main large image */}
      <Card className="relative overflow-hidden">
        <div className="aspect-[3/4] relative">
          <img 
            src={allImages[selectedIndex]} 
            alt={`${profileName} - Photo ${selectedIndex + 1}`} 
            className="w-full h-full object-cover"
          />
          
          {/* Navigation arrows */}
          {allImages.length > 1 && (
            <>
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/30 text-white hover:bg-black/50 rounded-full"
                onClick={() => navigateImage('prev')}
              >
                <ChevronLeft className="h-6 w-6" />
              </Button>
              <Button 
                variant="ghost" 
                size="icon" 
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/30 text-white hover:bg-black/50 rounded-full"
                onClick={() => navigateImage('next')}
              >
                <ChevronRight className="h-6 w-6" />
              </Button>
            </>
          )}
          
          {/* Image counter */}
          <div className="absolute bottom-2 right-2 bg-black/50 text-white text-xs px-2 py-1 rounded-full">
            {selectedIndex + 1} / {allImages.length}
          </div>
        </div>
      </Card>
      
      {/* Thumbnail row - only show actual thumbnails, not placeholders */}
      {allImages.length > 1 && (
        <div className="grid grid-cols-4 gap-2">
          {allImages.map((image, index) => (
            <div 
              key={index} 
              className={`aspect-square cursor-pointer overflow-hidden rounded-md ${selectedIndex === index ? 'ring-2 ring-pink-500' : 'ring-1 ring-gray-200'}`}
              onClick={() => setSelectedIndex(index)}
            >
              <img 
                src={image} 
                alt={`${profileName} thumbnail ${index + 1}`} 
                className="w-full h-full object-cover"
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProfileImageGallery;
